import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/Button';

interface SimpleCameraCaptureProps {
  onCapture: (file: File) => void;
  onCancel?: () => void;
}

export function SimpleCameraCapture({
  onCapture,
  onCancel,
}: SimpleCameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      console.log('🎥 [SIMPLE CAMERA] Démarrage de la caméra...');
      setIsLoading(true);
      setError(null);

      // Vérifier si le composant est toujours monté
      if (!videoRef.current) return;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      console.log('✅ [SIMPLE CAMERA] Stream obtenu avec succès');
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        console.log('🎬 [SIMPLE CAMERA] Lecture de la vidéo...');
        
        // Gérer l'erreur de lecture avec un timeout
        try {
          await videoRef.current.play();
        } catch (error: any) {
          console.warn('⚠️ [SIMPLE CAMERA] Erreur lecture vidéo:', error);
          // Ne pas afficher d'erreur pour AbortError (changement de page)
          if (error.name !== 'AbortError') {
            setError('Erreur de lecture vidéo');
          }
        }
        
        console.log('✅ [SIMPLE CAMERA] Vidéo en lecture');
        setIsLoading(false);
        setIsCapturing(true);
        console.log('🚀 [SIMPLE CAMERA] Capture démarrée');
      }
    } catch (err: any) {
      console.error('❌ [SIMPLE CAMERA] Erreur caméra:', err);
      // Ne pas afficher d'erreur pour AbortError (changement de page)
      if (err.name !== 'AbortError') {
        setError('Impossible d\'accéder à la caméra');
      }
      setIsLoading(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCapturing(false);
  }, []);

  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `scan-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          console.log('✅ [SIMPLE CAMERA] Image capturée avec succès');
          onCapture(file);
        } else {
          console.error('❌ [SIMPLE CAMERA] Aucun blob généré');
        }
      },
      'image/jpeg',
      0.9
    );
  }, [onCapture]);

  const handleCancel = useCallback(() => {
    console.log('🛑 [SIMPLE CAMERA] Annulation demandée');
    stopCamera();
    onCancel?.();
  }, [stopCamera, onCancel]);

  useEffect(() => {
    let mounted = true;
    
    const initializeCamera = async () => {
      console.log('🎬 [SIMPLE CAMERA] Montage du composant - Démarrage caméra');
      if (mounted) {
        await startCamera();
      }
    };

    initializeCamera();

    return () => {
      console.log('🧹 [SIMPLE CAMERA] Démontage du composant - Arrêt caméra');
      mounted = false;
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  return (
    <div className="camera-capture-simple flex flex-col h-full">
      <div className="relative flex-1 min-h-0">
        {/* Video stream */}
        <video
          ref={videoRef}
          className="w-full h-full max-h-[70vh] object-contain bg-black"
          playsInline
          muted
        />

        {/* Overlay canvas pour capture */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-0"
        />

        {/* Status indicators */}
        <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
          Mode: Capture simple
        </div>

        {error && (
          <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white">Chargement de la caméra...</div>
          </div>
        )}
      </div>

      {/* Controls - maintenant en dessous de la zone de capture */}
      <div className="flex justify-center space-x-4 p-4 bg-white border-t">
        <Button
          type="button"
          onClick={handleCancel}
          variant="outline"
          className="bg-white text-gray-800 border-gray-300"
        >
          Annuler
        </Button>
        <Button
          type="button"
          onClick={captureImage}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={isLoading}
        >
          {isLoading ? 'Chargement...' : 'Capturer'}
        </Button>
      </div>
    </div>
  );
}